import { DATA_PERMISSIONS } from '@/constants/permissions';

export type AdminRole = 'admin' | 'superadmin' | 'manager' | string;
export type DataAccessSection =
  | 'gold-funds'
  | 'gold-hub'
  | 'money'
  | 'cpi'
  | 'selected-portfolio'
  | 'industry-analysis';

export interface AdminAccessRule {
  allowAuthenticated?: boolean;
  managerOnly?: boolean;
  strictPermissions?: boolean;
  roles?: string[];
  permissions?: string[];
  permissionPrefixes?: string[];
}

export interface RouteAccessRule {
  path: string;
  exact?: boolean;
  rule: AdminAccessRule;
}

export const ACCESS_RULES = {
  dashboard: { allowAuthenticated: true },
  admins: { permissions: ['admins.manage'], permissionPrefixes: ['admins.'] },
  data: { permissions: ['data.manage'], permissionPrefixes: ['data.'] },
  optionsManagement: { roles: ['admin', 'manager'] },
  users: { permissionPrefixes: ['users.'] },
  finance: { permissionPrefixes: ['financial.', 'finance.'] },
  financialTransactions: {
    permissionPrefixes: ['financial.transactions.', 'finance.transactions.'],
  },
  financialDiscountCodes: {
    permissionPrefixes: ['financial.discount_codes.', 'finance.discount_codes.'],
  },
  subscriptions: { permissionPrefixes: ['subscriptions.'] },
  courses: { permissionPrefixes: ['courses.'] },
  courseCategories: { permissions: ['courses.categories'] },
  courseInstructors: { permissions: ['courses.instructors'] },
  courseOrders: { permissions: ['courses.orders'] },
  courseLicense: { permissions: ['courses.assign_course'] },
  coursePosts: { permissionPrefixes: ['courses.'] },
  analyses: { permissionPrefixes: ['analyses.'] },
  media: { permissionPrefixes: ['media.'] },
  tickets: { permissionPrefixes: ['tickets.'] },
  consulting: { roles: ['superadmin'], permissionPrefixes: ['consulting.'] },
  faq: { permissionPrefixes: ['qa.'] },
  notifications: { permissionPrefixes: ['notifications.'] },
  rag: { permissionPrefixes: ['rag.'] },
  aiAgentGroup: {
    permissionPrefixes: ['ai_agent.', 'rag.', 'credit.'],
  },
  aiAgentOverview: {
    strictPermissions: true,
    permissions: ['ai_agent.view'],
  },
  aiAgentUsers: {
    strictPermissions: true,
    permissions: ['ai_agent.users.view'],
  },
  aiAgentUsage: {
    strictPermissions: true,
    permissions: ['ai_agent.usage.view'],
  },
  aiAgentConversations: {
    strictPermissions: true,
    permissions: ['ai_agent.conversations.view'],
  },
  aiAgentSettings: {
    strictPermissions: true,
    permissions: [
      'ai_agent.settings.view',
      'ai_agent.health.view',
      'ai_agent.audit.view',
    ],
  },
  portfolio: { permissionPrefixes: ['portfolio.'] },
  portfolioManagement: { roles: ['superadmin', 'manager'] },
  aiCredits: { permissionPrefixes: ['credit.'] },
  referrals: { roles: ['admin', 'manager'] },
  campaigns: { roles: ['admin', 'manager'] },
  publicSupport: {
    strictPermissions: true,
    permissions: [
      'public_support.view_all',
      'public_support.view_assigned',
    ],
  },
} satisfies Record<string, AdminAccessRule>;

export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  { path: '/dashboard', exact: true, rule: ACCESS_RULES.dashboard },
  { path: '/admins', rule: ACCESS_RULES.admins },
  { path: '/data', rule: ACCESS_RULES.data },
  { path: '/options-management', rule: ACCESS_RULES.optionsManagement },
  { path: '/users', rule: ACCESS_RULES.users },
  { path: '/finance/transactions', rule: ACCESS_RULES.financialTransactions },
  { path: '/finance/discounts', rule: ACCESS_RULES.financialDiscountCodes },
  { path: '/finance', rule: ACCESS_RULES.finance },
  { path: '/subscriptions', rule: ACCESS_RULES.subscriptions },
  { path: '/courses/categories', rule: ACCESS_RULES.courseCategories },
  { path: '/courses/teachers', rule: ACCESS_RULES.courseInstructors },
  { path: '/courses/orders', rule: ACCESS_RULES.courseOrders },
  { path: '/courses/license', rule: ACCESS_RULES.courseLicense },
  { path: '/courses/posts', rule: ACCESS_RULES.coursePosts },
  { path: '/courses', rule: ACCESS_RULES.courses },
  { path: '/analysis', rule: ACCESS_RULES.analyses },
  { path: '/tickets', rule: ACCESS_RULES.tickets },
  { path: '/consulting', rule: ACCESS_RULES.consulting },
  { path: '/faq', rule: ACCESS_RULES.faq },
  { path: '/media', rule: ACCESS_RULES.media },
  { path: '/notifications', rule: ACCESS_RULES.notifications },
  { path: '/ai/agent-overview', exact: true, rule: ACCESS_RULES.aiAgentOverview },
  { path: '/ai/agent-users', exact: true, rule: ACCESS_RULES.aiAgentUsers },
  { path: '/ai/agent-usage', exact: true, rule: ACCESS_RULES.aiAgentUsage },
  {
    path: '/ai/agent-conversations',
    exact: true,
    rule: ACCESS_RULES.aiAgentConversations,
  },
  { path: '/ai/agent-settings', exact: true, rule: ACCESS_RULES.aiAgentSettings },
  { path: '/agent/rag', rule: ACCESS_RULES.rag },
  { path: '/portfolio', rule: ACCESS_RULES.portfolio },
  { path: '/portfolio-management', rule: ACCESS_RULES.portfolioManagement },
  { path: '/ai-credits', rule: ACCESS_RULES.aiCredits },
  { path: '/referrals', rule: ACCESS_RULES.referrals },
  { path: '/campaigns', rule: ACCESS_RULES.campaigns },
  { path: '/public-support', rule: ACCESS_RULES.publicSupport },
];

export const getAdminRole = (user: any): AdminRole => {
  return String(
    user?.adminRole || user?.more?.role || user?.role || '',
  ).toLowerCase();
};

export const getAdminPermissions = (user: any): string[] => {
  return Array.isArray(user?.permissions) ? user.permissions : [];
};

export const isManager = (user: any): boolean => getAdminRole(user) === 'manager';

export const isSuperAdmin = (user: any): boolean => getAdminRole(user) === 'superadmin';

export const isManagerOrSuperAdmin = (user: any): boolean => {
  return isManager(user) || isSuperAdmin(user);
};

export const isPermissionScopedAdmin = (user: any): boolean => {
  const role = getAdminRole(user);
  return role === 'admin' || role === 'superadmin';
};

export const hasExactPermission = (user: any, permission: string): boolean => {
  if (isManager(user) || isSuperAdmin(user)) return true;
  const target = permission.toLowerCase();
  return getAdminPermissions(user).some((existing) => {
    const normalized = existing.trim().toLowerCase();
    if (normalized === "*") return true;
    if (normalized.endsWith(".*")) {
      return target.startsWith(normalized.slice(0, -1));
    }
    return normalized === target;
  });
};

const DATA_SECTION_PERMISSIONS: Record<DataAccessSection, string> = {
  'gold-funds': DATA_PERMISSIONS.goldFunds,
  'gold-hub': DATA_PERMISSIONS.goldHub,
  money: DATA_PERMISSIONS.money,
  cpi: DATA_PERMISSIONS.cpi,
  'selected-portfolio': DATA_PERMISSIONS.selectedPortfolio,
  'industry-analysis': DATA_PERMISSIONS.industryAnalysis,
};

export const hasDataSectionAccess = (
  user: any,
  section: DataAccessSection,
): boolean => {
  return (
    hasExactPermission(user, DATA_PERMISSIONS.manage) ||
    hasExactPermission(user, DATA_SECTION_PERMISSIONS[section])
  );
};

export const hasAnyPermissionPrefix = (user: any, prefixes: string[]): boolean => {
  if (isManager(user)) return true;
  const permissions = getAdminPermissions(user);
  return prefixes.some((prefix) => permissions.some((permission) => permission.startsWith(prefix)));
};

export const hasAnyPermission = (user: any, permissions: string[]): boolean => {
  if (isManager(user)) return true;
  const userPermissions = getAdminPermissions(user);
  return permissions.some((permission) => userPermissions.includes(permission));
};

export const hasAdminAccess = (user: any, rule: AdminAccessRule): boolean => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (rule.allowAuthenticated) return true;
  if (isManager(user) && !rule.strictPermissions) return true;
  if (rule.managerOnly) return false;
  if (rule.roles?.includes(getAdminRole(user))) return true;
  if (rule.strictPermissions) {
    const exact = rule.permissions?.some((permission) =>
      hasStrictPermission(user, permission)
    ) ?? false;
    const prefixes = rule.permissionPrefixes?.some((prefix) =>
      getAdminPermissions(user).some((permission) =>
        permission.trim().toLowerCase().startsWith(prefix.toLowerCase())
      )
    ) ?? false;
    return exact || prefixes;
  }
  if (!isPermissionScopedAdmin(user)) return false;

  const hasExact = rule.permissions?.length
    ? hasAnyPermission(user, rule.permissions)
    : false;
  const hasPrefix = rule.permissionPrefixes?.length
    ? hasAnyPermissionPrefix(user, rule.permissionPrefixes)
    : false;

  return hasExact || hasPrefix;
};

export const hasStrictPermission = (
  user: any,
  permission: string,
): boolean => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const target = permission.trim().toLowerCase();
  return getAdminPermissions(user).some((existing) => {
    const normalized = existing.trim().toLowerCase();
    return (
      normalized === '*' ||
      normalized === target ||
      (normalized.endsWith('.*') &&
        target.startsWith(normalized.slice(0, -1)))
    );
  });
};

export const getRouteAccessRule = (pathname: string): AdminAccessRule | null => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const matchedRoute = ROUTE_ACCESS_RULES.find((route) => {
    if (route.exact) return normalizedPath === route.path;
    return normalizedPath === route.path || normalizedPath.startsWith(`${route.path}/`);
  });

  return matchedRoute?.rule ?? null;
};
